import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Star, Trophy, Zap, Target, AlertCircle, UserCheck } from 'lucide-react';

const CRITERIA = [
  { key: 'speed_score', label: 'Speed', weight: '40%', icon: Zap },
  { key: 'task_completion_score', label: 'Task Completion', weight: '20%', icon: Target },
  { key: 'delivery_score', label: 'Delivery', weight: '20%', icon: UserCheck },
  { key: 'errors_score', label: 'Errors (Inverse)', weight: '10%', icon: AlertCircle },
  { key: 'manager_input_score', label: 'Manager Input', weight: '10%', icon: Star },
];

export default function PerformanceTab() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [staff, setStaff] = useState([]);
  const [scoreDialog, setScoreDialog] = useState(null);
  const [form, setForm] = useState({ staff_id: '', speed_score: 5, task_completion_score: 5, delivery_score: 5, errors_score: 5, manager_input_score: 5, notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [lb, st] = await Promise.all([api.get('/performance/leaderboard'), api.get('/staff')]);
      setLeaderboard(lb.data);
      setStaff(st.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openScore = (staffMember) => {
    setForm({ staff_id: staffMember.staff_id || staffMember.id, speed_score: 5, task_completion_score: 5, delivery_score: 5, errors_score: 5, manager_input_score: 5, notes: '' });
    setScoreDialog(staffMember);
  };

  const submitScore = async () => {
    try {
      await api.post('/performance/score', form);
      toast.success('Performance scored');
      setScoreDialog(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const weighted = (f) => (f.speed_score * 0.4 + f.task_completion_score * 0.2 + f.delivery_score * 0.2 + f.errors_score * 0.1 + f.manager_input_score * 0.1).toFixed(1);
  const scoreColor = (s) => s >= 8 ? 'text-emerald-400' : s >= 6 ? 'text-gold' : s >= 4 ? 'text-amber-400' : 'text-red-400';
  const roleColors = { manager: 'bg-gold/20 text-gold', chef: 'bg-amber-500/20 text-amber-400', bartender: 'bg-blue-accent/20 text-blue-accent', shisha_expert: 'bg-purple-500/20 text-purple-400', waiter: 'bg-emerald-500/20 text-emerald-400' };

  return (
    <div>
      {/* Leaderboard */}
      <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-gold" /> Staff Leaderboard
      </h3>
      <div className="space-y-2 mb-6">
        {leaderboard.map((s, i) => (
          <div key={s.staff_id} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-sm card-hover" data-testid={`leaderboard-${s.staff_id}`}>
            <div className="flex items-center gap-3">
              <span className={`font-mono text-sm w-6 text-center ${i < 3 ? 'text-gold font-bold' : 'text-zinc-500'}`}>#{i + 1}</span>
              <div>
                <p className="text-white font-sans text-sm">{s.name || 'Unnamed'}</p>
                <Badge className={`${roleColors[s.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>{s.role}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className={`font-mono text-lg font-bold ${scoreColor(s.average)}`}>{s.average.toFixed(1)}</p>
                <p className="text-zinc-600 text-xs font-sans">{s.reviews} reviews</p>
              </div>
              <Button size="sm" onClick={() => openScore(s)} className="h-7 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 rounded-sm font-sans text-xs" data-testid={`score-staff-${s.staff_id}`}>
                <Star className="w-3 h-3 mr-1" /> Score
              </Button>
            </div>
          </div>
        ))}
        {leaderboard.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-performance">No performance data yet. Score your first staff member.</p>}
      </div>

      {/* Score Dialog */}
      <Dialog open={!!scoreDialog} onOpenChange={() => setScoreDialog(null)}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-white">Score: {scoreDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {CRITERIA.map(c => (
              <div key={c.key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans flex items-center gap-1">
                    <c.icon className="w-3 h-3" /> {c.label} ({c.weight})
                  </Label>
                  <span className={`font-mono text-sm font-bold ${scoreColor(form[c.key])}`}>{form[c.key]}</span>
                </div>
                <Slider
                  value={[form[c.key]]}
                  onValueChange={([v]) => setForm(p => ({ ...p, [c.key]: v }))}
                  min={0} max={10} step={0.5}
                  className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-gold"
                  data-testid={`slider-${c.key}`}
                />
              </div>
            ))}
            <div className="pt-2 border-t border-white/5 flex items-center justify-between">
              <span className="text-zinc-400 font-sans text-sm">Weighted Score</span>
              <span className={`font-mono text-xl font-bold ${scoreColor(parseFloat(weighted(form)))}`}>{weighted(form)}/10</span>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Optional notes" data-testid="performance-notes-input" />
            </div>
            <Button onClick={submitScore} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-performance-button">Submit Score</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
